import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { flushSync } from 'react-dom';
import { DEFAULT_THEME, SYSTEM_THEMES, THEMES, type ThemeChoice, type ThemeId } from './themes';

// Applies the chosen theme to <html data-theme>, follows the device for `system`, remembers the
// choice, and animates changes as a circle of light spreading from where the user clicked.
// index.html applies the saved theme before first paint so there is never a flash.

const STORAGE_KEY = 'clairo.theme';

interface ThemeContextValue {
  choice: ThemeChoice;
  resolved: ThemeId;
  setTheme: (choice: ThemeChoice, origin?: { x: number; y: number }) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const darkQuery = () => window.matchMedia('(prefers-color-scheme: dark)');
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function resolve(choice: ThemeChoice, prefersDark: boolean): ThemeId {
  return choice === 'system' ? SYSTEM_THEMES[prefersDark ? 'dark' : 'light'] : choice;
}

function readChoice(): ThemeChoice {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'system' || (saved && saved in THEMES)) return saved as ThemeChoice;
  } catch {
    // Storage blocked.
  }
  return DEFAULT_THEME;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<ThemeChoice>(readChoice);
  const [prefersDark, setPrefersDark] = useState(() => darkQuery().matches);
  const resolved = resolve(choice, prefersDark);

  useEffect(() => {
    const query = darkQuery();
    const onChange = (e: MediaQueryListEvent) => setPrefersDark(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', THEMES[resolved].bg);
  }, [resolved]);

  const setTheme = useCallback((next: ThemeChoice, origin?: { x: number; y: number }) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not remembered; still applied.
    }
    const apply = () => {
      flushSync(() => setChoice(next));
      document.documentElement.dataset.theme = resolve(next, darkQuery().matches);
    };
    if (!document.startViewTransition || reducedMotion()) {
      apply();
      return;
    }
    const root = document.documentElement.style;
    root.setProperty('--reveal-x', origin ? `${origin.x}px` : '50%');
    root.setProperty('--reveal-y', origin ? `${origin.y}px` : '0%');
    document.startViewTransition(apply);
  }, []);

  const value = useMemo(() => ({ choice, resolved, setTheme }), [choice, resolved, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme outside ThemeProvider');
  return ctx;
}
